import { ResumeApplicationPage } from '@resurs-internal/deposits-app-components';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';

import { type ApplicationHandlerProps } from '@/components';
import { CountryContext } from '@/contexts';
import { isAuthenticated, sendGTMApplicationStatus } from '@/helpers';
import { useApplication } from '@/hooks';
import { GTMStatus } from '@/models';
import { signApplication } from '@/services';
import { ApplicationStatus } from '@/types/consumer-deposit-application-service';

export const ResumeApplicationView = () => {
  const { applicationId, product } =
    useOutletContext() as ApplicationHandlerProps;
  const navigate = useNavigate();
  const { country } = useContext(CountryContext);
  const { t } = useTranslation();

  const { application, isLoading } = useApplication(applicationId);

  const handleGTMStatus = useCallback(
    (
      applicationId: string,
      productVariant: string,
      stepName: string,
      status: GTMStatus,
    ) => {
      sendGTMApplicationStatus({
        applicationId,
        country,
        productVariant,
        status,
        stepName,
      });
    },
    [country],
  );

  useEffect(() => {
    if (!isLoading) {
      switch (application.status) {
        case ApplicationStatus.PROCESSING:
          navigate(`/application/${applicationId}`);
          break;
        case ApplicationStatus.REJECTED:
          handleGTMStatus(
            applicationId,
            product.name,
            t('status-page.denied.title'),
            GTMStatus.REJECTED,
          );
          navigate('/denied');
          break;
        case ApplicationStatus.PENDING:
          handleGTMStatus(
            applicationId,
            product.name,
            t('status-page.pending.title'),
            GTMStatus.MANUAL_INSPECTION,
          );
          navigate(`/application/${applicationId}/pending`);
          break;
        default:
          handleGTMStatus(
            applicationId,
            product.name,
            t('category.resume-application.approved.title'),
            GTMStatus.APPROVED,
          );
          break;
      }
    }
  }, [
    isLoading,
    application,
    product,
    applicationId,
    navigate,
    t,
    handleGTMStatus,
  ]);

  const sign = () => {
    if (isAuthenticated() && applicationId) {
      const onSuccessUri = `${window.location.protocol}//${window.location.host}/application/${applicationId}/deposit-account`;
      const onCancelledUri = `${window.location.protocol}//${window.location.host}/application/${applicationId}/summary`;

      return signApplication(applicationId, onSuccessUri, onCancelledUri)
        .then((response) => {
          if (response._links['sign-redirect'].href)
            window.location.assign(response._links['sign-redirect'].href);
          else navigate('/error');
        })
        .catch((error) => {
          console.error(error);
          navigate('/error');
        });
    } else {
      navigate('/error');
    }
  };

  return (
    <ResumeApplicationPage
      country={country}
      isLoading={isLoading}
      sign={sign}
    />
  );
};
